<template>
  <div class="p-3 w-100">
    <h2>{{ showGlobal ? $t('filter.globalExportprofiles') : $t('filter.localExportprofiles') }}</h2>
    <div
        class="settings-button d-inline-block mr-1"
        @click="createNew = true"
    >
      {{ $t('filter.create') }} <icon class="ml-1" type="plus" />
    </div>
    <div
        class="settings-button d-inline-block"
        @click="loadPaginatedData(0)"
    >
      {{ $t('filter.reload') }} <icon class="ml-1" type="rotate-right"/>
    </div>
    <portal to="filterPortal">
      <content-filter
          :id="listName"
          :limit-to-organization-ids="[organizationId]"
          :org-filter-local="true"
          :org-filtering="true"
          :tag-filtering="true"
          :team-filtering="true"
          class="mb-2"
          store-name="Filter"
          @filter="filtering"
      >
        <tile-button
            ref="globalLocal"
            :icons="true"
            :initial-active-tiles="[showGlobal ? 'global' : 'assembly']"
            :is-multi-select="false"
            :tiles="tileNames"
            :values="tileValues"
            class="mb-2 mt-3"
            @trigger="setGlobal"
        />
      </content-filter>
    </portal>
    <pagination
        id="exportprofileList"
        :limit="paginationLimit"
        :total="$store.getters.getFilterListOptions($route.params.id, 'pagination_items')"
        @setActive="loadPaginatedData"
    />
    <div class="w-100 mb-2"/>
    <export-profile-item
        v-if="createNew"
        key="new"
        :assembly-id="assemblyId"
        :organization-id="organizationId"
        :profile="{
          id: 'new',
          name: 'new',
          displayName: 'new',
          description: '',
          sourceProjectId: projectId,
          instances: []
        }"
        :project-id="projectId"
        class="mb-2 w-100"
        @aborted="createNew = false"
        @showPreview="showPreview('new')"
        @created="created"
    />
    <export-profile-item
        v-for="profile in sortedByInstances"
        v-if="!currentlyEditing || currentlyEditing === profile.id"
        :key="profile.id + forceReRenderKey"
        :assembly-id="assemblyId"
        :input-units="inputUnits"
        :organization-id="organizationId"
        :profile="profile"
        :project-id="projectId"
        :root-node="rootNode"
        :linked-export-profiles="linkedExportProfiles"
        :highlight="freshlyAddedProfiles.includes(profile.id)"
        class="mb-2 w-100"
        @currentlyEditing="(val) => { currentlyEditing = val }"
        @deleted="deleted"
        @reloadData="loadPaginatedData(0, false)"
        @reloadItem="reloadItem"
        @showPreview="showPreview(profile.id)"
        @instanceUpdated="instanceUpdated"
        @instanceDeleted="instanceDeleted"
    />
    <div v-if="!Object.keys(sortedByInstances).length && !loading" class="not-found mt-2">
      <p class="lighter">{{ $t('noExportProfilesFound') }}</p>
    </div>
    <loading-spinner v-if="loading" class="mt-5"/>
  </div>
</template>

<script>
import ExportProfileItem from "@/components/exportProfiles/ExportProfileItem.vue";
import DateMixin from "@/components/mixins/DateMixin.js.vue";
import LoadOverviewMixinJs from "@/components/mixins/LoadOverviewMixin.js.vue";
import ContentFilter from "@/components/widgets/filter/ContentFilter.vue";
import Pagination from "@/components/Pagination.vue";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import TileButton from "@/components/forms/TileButton.vue";
import Icon from "@/components/Icon.vue";
export default {
  name: "IntegratedExportProfiles",
  components: {Pagination, ContentFilter, ExportProfileItem, LoadingSpinner, TileButton, Icon},
  mixins: [DateMixin, LoadOverviewMixinJs],
  props: {
    organizationId: {type: String, required: true},
    projectId: {type: String, required: true},
    assemblyId: {type: String, required: true},
    rootNode: {type: Object, required: true},
    inputUnits: {type: String, required: true},
    linkedExportProfiles: {type: Array, required: false, default: () => []},
  },
  data() {
    return {
      currentlyEditing: null,
      paginationLimit: 80,
      showGlobal: false,
      searchString: '',
      viewId: 'mainContent',
      forceReRenderKey: 0,
      include: 'instances,teams,tags',
      registerMethod: 'registerFilterQueryParams',
      loadMethod: 'loadFilters',
      listName: this.projectId,
      deleteMethod: 'deleteFilter',
      list: {},
      filter: {},
      queryRelevantComponents: 2, //pagination and filter
      teams: [],
      customfilter: null,
      customFilter: {},
      createNew: false,
      freshlyAddedProfiles: [],
      tileValues: ['assembly', 'global'],
      tileNames: ['filter.limitedToAssembly', 'filter.limitedToGlobal'],
    }
  },
  computed: {
    sortedByInstances() {
      const items = this.$store.getters.getFiltersByListName(this.listName);
      if (!items || !Object.keys(items).length) return [];
      let sortedObject = {};
      Object.values(items).sort((a, b) => {
        const aFresh = this.freshlyAddedProfiles.includes(a.id);
        const bFresh = this.freshlyAddedProfiles.includes(b.id);

        // Fresh ones FIRST
        if (aFresh && !bFresh) return -1;
        if (bFresh && !aFresh) return 1;

        if (!a.instances) return 1;
        if (!b.instances) return -1;
        const filteredAInstances = a.instances.filter(i => i.projectId === this.projectId);
        const filteredBInstances = b.instances.filter(i => i.projectId === this.projectId);
        if (!filteredAInstances.length) return 1;
        if (!filteredBInstances.length) return -1;
        return filteredBInstances.length - filteredAInstances.length
      }).forEach(item => {
        sortedObject[item.id] = item;
      })
      return sortedObject;
    },
  },
  watch: {
    async showGlobal() {
      await this.changeQueryOnGlobal()
      await this.loadPaginatedData(0)
      this.currentlyEditing = null;
    },
    currentlyEditing(val) {
      if(!val) {
        this.showPreview(null);
      }
    }
  },
  beforeMount() {
    this.changeQueryOnGlobal()
  },
  methods: {
    setTileButtonsGlobal(isGlobal = false) {
      console.log('setTileButtonsGlobal', isGlobal);
      console.log('refs', this.$refs);
      if(!isGlobal) {
        this.$refs.globalLocal.trigger(this.tileValues[0], this.tileNames[0])
      } else {
        this.$refs.globalLocal.trigger(this.tileValues[1], this.tileNames[1])
      }
    },
    created(asset) {
      console.log('created integrated export profiles', asset.id);
      this.createNew = false;
      this.freshlyAddedProfiles.push(asset.id);
      this.setTileButtonsGlobal(!asset.sourceProjectId);
      this.showPreview(null);
      this.loadPaginatedData(0);
    },
    instanceUpdated(id) {
      console.log('instanceUpdated integrated export profiles', id);
      this.showPreview(null);
      this.$emit('instanceUpdated', [id]);
    },
    showPreview(profileId) {
      this.$emit('currentlyEditing', profileId);
    },
    async reloadItem(args) {
      const {isGlobal} = args;
      console.log('reloadItem aargs', args);
      if(isGlobal !== undefined) {
        this.setTileButtonsGlobal(isGlobal);
      }
      this.showPreview(null);
      this.loadPaginatedData(0)
    },
    deleted(id) {
      // todo: send an instanceDeleted for all instances of this profile? afterwards
      this.showPreview(null);
      this.loadPaginatedData(0)
      this.$emit('deleted', id);
      this.currentlyEditing = null;
    },
    instanceDeleted(id) {
      this.$emit('instanceDeleted', id);
      this.currentlyEditing = null;
    },
    setGlobal(tiles) {
      const {value} = tiles;
      this.showGlobal = value === 'global';
    },
    async changeQueryOnGlobal() {
      const specialKey = this.listName;
      let args = {
        listName: this.listName,
      }
      if (this.showGlobal) {
        args.add = {
          filter: {
            [specialKey]: 'sourceProjectId eq null'
          }
        }
      } else {
        args.add = {
          filter: {
            [specialKey]: 'sourceProjectId eq ' + this.projectId
          }
        }
      }
      this.customFilter = args.add.filter[specialKey];
    },
  },
}
</script>